import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useContext } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import {
  Calendar,
  Users,
  Building2,
  Stethoscope,
  Activity,
  Clock,
  TrendingUp,
  UserPlus
} from 'lucide-react';
import { appointmentsService } from '@/services/api/appointments.service';
import { patientsService } from '@/services/api/patients.service';
import { clinicsService } from '@/services/api/clinics.service';
import { doctorsService } from '@/services/api/doctors.service';
import type { Appointment } from '@/types';

export function Dashboard() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { clinic, clinicId, doctor, doctorId } = useContext();
  
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [activePatientsCount, setActivePatientsCount] = useState(0);
  const [clinicsCount, setClinicsCount] = useState(0);
  const [doctorsCount, setDoctorsCount] = useState(0);
  const [totalPatientsCount, setTotalPatientsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [doctorId, clinicId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load today's appointments for current doctor
      if (doctorId) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const appointmentsResponse = await appointmentsService.getAll({
          doctorId,
          from: today.toISOString(),
          to: tomorrow.toISOString(),
          limit: 100,
        });
        setTodayAppointments(appointmentsResponse.data);
        
        // Load active patients for current doctor
        const patientsResponse = await patientsService.getAll({ limit: 10000 });
        const doctorPatients = patientsResponse.data.filter(p => 
          p.doctorIds && p.doctorIds.includes(doctorId) && p.isActive !== false
        );
        setActivePatientsCount(doctorPatients.length);
      }
      
      // Load system-level data for admin
      if (isAdmin) {
        const [clinicsResponse, doctorsResponse, allPatientsResponse] = await Promise.all([
          clinicsService.getAll({ limit: 1000 }),
          doctorsService.getAll({ limit: 1000 }),
          patientsService.getAll({ limit: 10000 }),
        ]);

        // All data belongs to this deployment's single organization
        const orgClinics = clinicsResponse.data;
        const orgPatients = allPatientsResponse.data;
        const orgDoctors = doctorsResponse.data;
        
        setClinicsCount(orgClinics.length);
        setDoctorsCount(orgDoctors.length);
        setTotalPatientsCount(orgPatients.length);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: 'Agendamentos Hoje',
      value: loading ? '-' : todayAppointments.length.toString(),
      icon: Calendar,
      change: doctorId ? 'Do médico atual' : 'Selecione um médico',
      changeType: 'neutral' as const,
      color: 'blue' as const
    },
    {
      title: 'Pacientes Ativos',
      value: loading ? '-' : activePatientsCount.toString(),
      icon: Users,
      change: doctorId ? 'Do médico atual' : 'Selecione um médico',
      changeType: 'neutral' as const,
      color: 'green' as const
    },
    {
      title: 'Taxa de Comparecimento',
      value: '92%',
      icon: Activity,
      change: 'Em breve',
      changeType: 'neutral' as const,
      color: 'purple' as const
    },
    {
      title: 'Tempo Médio de Consulta',
      value: '32 min',
      icon: Clock,
      change: 'Em breve',
      changeType: 'neutral' as const,
      color: 'orange' as const
    },
  ];

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `Há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  };

  const recentActivities = todayAppointments.slice(0, 3).map(apt => ({
    id: apt.id,
    type: 'appointment',
    message: `Consulta: ${apt.title}`,
    time: formatTimeAgo(apt.startUtc),
    icon: Calendar,
  }));

  const iconColors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Bem-vindo de volta, {user?.name || 'Usuário'}!
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {clinic && doctor ? (
            <>
              Você está acessando como Dr(a). {doctor.name} em {clinic.name}
            </>
          ) : clinic ? (
            <>Você está na clínica {clinic.name}</>
          ) : (
            'Selecione uma clínica e médico para começar'
          )}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} variant="bordered">
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${iconColors[stat.color]}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="ml-5 flex-1">
                  <p className="text-sm font-medium text-gray-500">
                    {stat.title}
                  </p>
                  <div className="flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                  <p className="text-xs mt-1 text-gray-600">
                    {stat.change}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activities */}
        <Card variant="bordered">
          <CardHeader>
            <CardTitle>Atividades Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  Carregando...
                </div>
              ) : recentActivities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma atividade recente
                </div>
              ) : (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <activity.icon className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {activity.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {activity.time}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card variant="bordered">
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => navigate('/calendar?new=true')}
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Calendar className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">
                  Novo Agendamento
                </span>
              </button>
              <button 
                onClick={() => navigate('/admin/pacientes?new=true')}
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <UserPlus className="h-8 w-8 text-green-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">
                  Cadastrar Paciente
                </span>
              </button>
              <button 
                disabled
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg opacity-50 cursor-not-allowed"
              >
                <Activity className="h-8 w-8 text-purple-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">
                  Ver Prontuários
                </span>
                <span className="text-xs text-gray-500 mt-1">Em breve</span>
              </button>
              <button 
                disabled
                className="flex flex-col items-center p-4 bg-gray-50 rounded-lg opacity-50 cursor-not-allowed"
              >
                <TrendingUp className="h-8 w-8 text-orange-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">
                  Relatórios
                </span>
                <span className="text-xs text-gray-500 mt-1">Em breve</span>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Quick Stats - Only for Admins */}
      {isAdmin && (
        <Card variant="bordered">
          <CardHeader>
            <CardTitle>Visão Geral do Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                <Building2 className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {loading ? '-' : clinicsCount}
                  </p>
                  <p className="text-sm text-gray-600">Clínicas Ativas</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                <Stethoscope className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {loading ? '-' : doctorsCount}
                  </p>
                  <p className="text-sm text-gray-600">Médicos Cadastrados</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg">
                <Users className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {loading ? '-' : totalPatientsCount}
                  </p>
                  <p className="text-sm text-gray-600">Total de Pacientes</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}